import { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Checkbox, FormControlLabel, Link, useMediaQuery, useTheme } from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';

// Use unified date utils
import { moment, formatDateTime } from '../../utils/dateUtils';

const fontFamily = '"Inter", sans-serif';

const ProposalViewSignature = ({ 
  signature, 
  onSign, 
  token, 
  isAccepted,
  customerName,
  isAuthUser = false // New prop: true if user is authenticated via JWT
}) => {
  const [agreement, setAgreement] = useState(false);
  const signatureRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 150 });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Check if user can sign (either has client token OR is authenticated user with proposals permission)
  const canSign = token || isAuthUser;

  // Responsive canvas size
  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasContainerRef.current) {
        const containerWidth = canvasContainerRef.current.offsetWidth - 32; // padding
        const maxWidth = Math.min(containerWidth, 500);
        const width = isMobile ? Math.min(maxWidth, 320) : isTablet ? Math.min(maxWidth, 400) : 400;
        const height = isMobile ? 120 : 150;
        setCanvasSize({ width, height });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [isMobile, isTablet]);

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleAccept = () => {
    if (signatureRef.current && onSign) {
      const signData = signatureRef.current.toDataURL();
      onSign(signData);
    }
  };

  const noPrint = {
    '@media print': {
      display: 'none',
    },
  };

  return (
    <Box sx={{ mb: 4, px: isMobile ? 1 : 2 }}>
      <Typography
        sx={{
          fontFamily,
          fontWeight: 600,
          fontSize: isMobile ? 14 : 16,
          color: '#1a1a1a',
          mb: 2,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        Signature
      </Typography>

      {/* If already signed, show signature */}
      {isAccepted && signature ? (
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              backgroundColor: '#f8f9fa',
              display: 'inline-block',
              maxWidth: '100%',
            }}
          >
            <img
              src={signature.sign}
              alt="Customer Signature"
              style={{ maxWidth: '100%', height: 'auto', maxHeight: isMobile ? 80 : 100 }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily,
              fontSize: isMobile ? 11 : 12,
              color: '#666',
              mt: 1,
            }}
          >
            Signed on {formatDateTime(signature.createdAt, 'MMMM D, YYYY [at] h:mm A')}
          </Typography>
        </Box>
      ) : (
        /* If not signed, show signature pad */
        <Box sx={noPrint}>
          <Box
            ref={canvasContainerRef}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              backgroundColor: '#fff',
              mb: 2,
            }}
          >
            <Box sx={{ p: isMobile ? 1.5 : 2, borderBottom: '1px solid #e0e0e0' }}>
              <Typography
                sx={{
                  fontFamily,
                  fontSize: isMobile ? 11 : 12,
                  color: '#666',
                }}
              >
                {canSign 
                  ? 'Please sign below to accept this proposal' 
                  : 'Signature requires authorization'}
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                py: isMobile ? 1.5 : 2,
                px: isMobile ? 1 : 2,
                touchAction: 'none', // Prevent scroll while drawing on mobile
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  style: {
                    border: '1px dashed #ccc',
                    borderRadius: 4,
                    backgroundColor: '#fafafa',
                    touchAction: 'none', // Critical for mobile drawing
                  },
                }}
                velocityFilterWeight={0.7}
                minWidth={isMobile ? 1 : 0.5}
                maxWidth={isMobile ? 3 : 2.5}
              />
            </Box>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            gap: isMobile ? 1.5 : 2 
          }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreement}
                  onChange={(e) => setAgreement(e.target.checked)}
                  sx={{ color: '#0d3c61' }}
                  disabled={!canSign}
                />
              }
              label={
                <Typography sx={{ fontFamily, fontSize: isMobile ? 11 : 12, color: '#333' }}>
                  I agree to the{' '}
                  <Link
                    href="https://airmaxx.com/wp-content/uploads/2022/06/Home-Improvement-Construction-Contract-_-Owner-and-Contractor.pdf"
                    target="_blank"
                    rel="noReferrer"
                    sx={{ color: '#0d3c61' }}
                  >
                    Terms and Conditions
                  </Link>
                </Typography>
              }
              sx={{ 
                m: 0,
                '& .MuiFormControlLabel-label': {
                  ml: isMobile ? 0.5 : 1,
                }
              }}
            />

            <Box sx={{ 
              display: 'flex', 
              gap: isMobile ? 1.5 : 2,
              flexDirection: isMobile ? 'column' : 'row',
              width: isMobile ? '100%' : 'auto',
            }}>
              <Button
                variant="outlined"
                size={isMobile ? 'medium' : 'small'}
                onClick={handleClear}
                fullWidth={isMobile}
                sx={{
                  fontFamily,
                  textTransform: 'none',
                  borderColor: '#ccc',
                  color: '#666',
                  py: isMobile ? 1.5 : 1,
                }}
              >
                Clear Signature
              </Button>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'small'}
                onClick={handleAccept}
                disabled={!agreement || !canSign}
                fullWidth={isMobile}
                sx={{
                  fontFamily,
                  textTransform: 'none',
                  backgroundColor: '#0d3c61',
                  py: isMobile ? 1.5 : 1,
                  '&:hover': {
                    backgroundColor: '#0a2d4a',
                  },
                }}
              >
                Accept Proposal
              </Button>
            </Box>

            {!canSign && (
              <Typography
                sx={{
                  fontFamily,
                  fontSize: isMobile ? 10 : 11,
                  color: '#999',
                  fontStyle: 'italic',
                  textAlign: 'center',
                }}
              >
                You need a valid token or login to sign this proposal
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Date line for print */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          gap: isMobile ? 2 : 4,
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily, fontSize: isMobile ? 10 : 11, color: '#666', mb: 0.5 }}>
              Date
            </Typography>
            <Box sx={{ borderBottom: '1px solid #333', minHeight: 24 }}>
              {signature?.createdAt && (
                <Typography sx={{ fontFamily, fontSize: isMobile ? 11 : 12 }}>
                  {formatDateTime(signature.createdAt, 'MM/DD/YYYY')}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: isMobile ? 1 : 2 }}>
            <Typography sx={{ fontFamily, fontSize: isMobile ? 10 : 11, color: '#666', mb: 0.5 }}>
              Customer Name
            </Typography>
            <Box sx={{ borderBottom: '1px solid #333', minHeight: 24 }}>
              {customerName && (
                <Typography sx={{ fontFamily, fontSize: isMobile ? 11 : 12 }}>
                  {customerName}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProposalViewSignature;
